const AUDIO_BACKGROUND_IMAGE_MAX_WIDTH = 1280;
const AUDIO_BACKGROUND_IMAGE_MAX_HEIGHT = 720;
const AUDIO_BACKGROUND_IMAGE_MIME_TYPE = "image/jpeg";
const AUDIO_BACKGROUND_IMAGE_QUALITY = 0.72;

interface CoverImageRect {
  sx: number;
  sy: number;
  sw: number;
  sh: number;
  dx: number;
  dy: number;
  dw: number;
  dh: number;
}

function coverImageRect(
  sourceWidth: number,
  sourceHeight: number,
  targetWidth: number,
  targetHeight: number
): CoverImageRect {
  if (sourceWidth <= 0 || sourceHeight <= 0 || targetWidth <= 0 || targetHeight <= 0) {
    return { sx: 0, sy: 0, sw: 0, sh: 0, dx: 0, dy: 0, dw: 0, dh: 0 };
  }

  const sourceAspect = sourceWidth / sourceHeight;
  const targetAspect = targetWidth / targetHeight;

  if (sourceAspect > targetAspect) {
    const sw = sourceHeight * targetAspect;
    return {
      sx: (sourceWidth - sw) / 2,
      sy: 0,
      sw,
      sh: sourceHeight,
      dx: 0,
      dy: 0,
      dw: targetWidth,
      dh: targetHeight,
    };
  }

  const sh = sourceWidth / targetAspect;
  return {
    sx: 0,
    sy: (sourceHeight - sh) / 2,
    sw: sourceWidth,
    sh,
    dx: 0,
    dy: 0,
    dw: targetWidth,
    dh: targetHeight,
  };
}

export function drawCoverImage(
  ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
  image: CanvasImageSource,
  targetWidth: number,
  targetHeight: number
) {
  const sourceWidth = imageSourceWidth(image);
  const sourceHeight = imageSourceHeight(image);
  const rect = coverImageRect(Number(sourceWidth), Number(sourceHeight), targetWidth, targetHeight);
  if (!rect.sw || !rect.sh || !rect.dw || !rect.dh) return;
  ctx.drawImage(image, rect.sx, rect.sy, rect.sw, rect.sh, rect.dx, rect.dy, rect.dw, rect.dh);
}

function imageSourceWidth(image: CanvasImageSource): number {
  if ("videoWidth" in image) return image.videoWidth;
  if ("naturalWidth" in image) return image.naturalWidth;
  if ("displayWidth" in image) return image.displayWidth;
  if ("width" in image) return Number(image.width);
  return 0;
}

function imageSourceHeight(image: CanvasImageSource): number {
  if ("videoHeight" in image) return image.videoHeight;
  if ("naturalHeight" in image) return image.naturalHeight;
  if ("displayHeight" in image) return image.displayHeight;
  if ("height" in image) return Number(image.height);
  return 0;
}

async function imageFromFile(file: File): Promise<HTMLImageElement | ImageBitmap> {
  if (!file.type.startsWith("image/")) {
    throw new Error("Choose an image file.");
  }
  if (typeof createImageBitmap === "function") {
    return createImageBitmap(file, { imageOrientation: "from-image" });
  }
  if (typeof Image === "undefined") {
    throw new Error("This browser cannot decode the background image.");
  }

  return new Promise<HTMLImageElement>((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not load the background image."));
    };
    img.src = url;
  });
}

function dataUrlFromCanvas(canvas: HTMLCanvasElement): string {
  return canvas.toDataURL(AUDIO_BACKGROUND_IMAGE_MIME_TYPE, AUDIO_BACKGROUND_IMAGE_QUALITY);
}

/**
 * Compress a user-picked image before storing it on the edit doc. Keeping this
 * hard-capped matters because the document is persisted in IndexedDB and may
 * eventually be sent over render APIs; v1 still defers Lambda for this field.
 */
export async function encodeAudioBackgroundImageDataUrl(file: File): Promise<string> {
  const image = await imageFromFile(file);
  try {
    const sourceWidth = imageSourceWidth(image);
    const sourceHeight = imageSourceHeight(image);
    const scale = Math.min(
      1,
      AUDIO_BACKGROUND_IMAGE_MAX_WIDTH / sourceWidth,
      AUDIO_BACKGROUND_IMAGE_MAX_HEIGHT / sourceHeight
    );
    const width = Math.max(1, Math.round(sourceWidth * scale));
    const height = Math.max(1, Math.round(sourceHeight * scale));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Could not prepare the background image.");
    ctx.drawImage(image, 0, 0, width, height);
    return dataUrlFromCanvas(canvas);
  } finally {
    if ("close" in image) image.close();
  }
}

export async function decodeAudioBackgroundImage(
  dataUrl: string
): Promise<HTMLImageElement | ImageBitmap> {
  if (typeof createImageBitmap === "function") {
    const response = await fetch(dataUrl);
    const blob = await response.blob();
    return createImageBitmap(blob);
  }
  if (typeof Image === "undefined") {
    throw new Error("This browser cannot decode the background image.");
  }

  return new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Could not load the background image."));
    img.src = dataUrl;
  });
}
